import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { propertyApi, contactApi, authApi, testimonialApi } from "./api";
import {
  Property
} from "./types";

// Property hooks
export function useProperties() {
  return useQuery({
    queryKey: ["properties"],
    queryFn: propertyApi.getProperties,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useProperty(id: string) {
  return useQuery({
    queryKey: ["property", id],
    queryFn: () => propertyApi.getProperty(id),
    enabled: !!id,
  });
}

export function useCreateProperty() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      propertyData,
      imageFiles,
      videoFile,
    }: {
      propertyData: Omit<Property, "id" | "createdAt" | "updatedAt">;
      imageFiles?: File[];
      videoFile?: File;
    }) => propertyApi.createProperty(propertyData, imageFiles, videoFile),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
    },
  });
}

export function useUpdateProperty() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
      imageFiles,
      existingImagePaths,
      videoFile,
      keepExistingVideo,
    }: {
      id: string;
      data: Partial<Property>;
      imageFiles?: File[];
      existingImagePaths?: string[];
      videoFile?: File;
      keepExistingVideo?: boolean;
    }) =>
      propertyApi.updateProperty(
        id,
        data,
        imageFiles,
        existingImagePaths,
        videoFile,
        keepExistingVideo
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
    },
  });
}

export function useDeleteProperty() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: propertyApi.deleteProperty,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
    },
  });
}

// Contact hooks
export function useContactForm() {
  return useMutation({
    mutationFn: contactApi.sendContactForm,
  });
}

export function useGeneralContactForm() {
  return useMutation({
    mutationFn: contactApi.sendGeneralContactForm,
  });
}

export function useContacts(
  page: number = 1,
  isRead?: boolean,
  propertyId?: number
) {
  return useQuery({
    queryKey: ["contacts", page, isRead, propertyId],
    queryFn: () => contactApi.getContacts(page, 20, isRead, propertyId),
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useContact(id: number) {
  return useQuery({
    queryKey: ["contact", id],
    queryFn: () => contactApi.getContact(id),
    enabled: !!id,
  });
}

export function useContactStats() {
  return useQuery({
    queryKey: ["contact-stats"],
    queryFn: contactApi.getContactStats,
    staleTime: 60 * 1000, // 1 minute
  });
}

export function useMarkContactAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: contactApi.markContactAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["contact-stats"] });
    },
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: contactApi.deleteContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["contact-stats"] });
    },
  });
}

// Auth hooks
export function useAuth() {
  const queryClient = useQueryClient();

  const checkAuth = useQuery({
    queryKey: ["auth"],
    queryFn: authApi.checkAuth,
    retry: false,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const loginMutation = useMutation({
    mutationFn: ({
      username,
      password,
    }: {
      username: string;
      password: string;
    }) => authApi.login(username, password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth"] });
    },
  });

  const googleLoginMutation = useMutation({
    mutationFn: ({ credential }: { credential: string }) =>
      authApi.googleLogin(credential),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth"] });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth"] });
    },
  });

  return {
    isAuthenticated: checkAuth.data?.authenticated ?? false,
    isLoading: checkAuth.isLoading,
    login: loginMutation.mutate,
    googleLogin: googleLoginMutation.mutate,
    logout: logoutMutation.mutate,
    isLoginLoading: loginMutation.isPending || googleLoginMutation.isPending,
    loginError: loginMutation.error || googleLoginMutation.error,
  };
}

// Testimonial hooks
export function useApprovedTestimonials() {
  return useQuery({
    queryKey: ["testimonials", "approved"],
    queryFn: testimonialApi.getApprovedTestimonials,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useAllTestimonials() {
  return useQuery({
    queryKey: ["testimonials", "all"],
    queryFn: testimonialApi.getAllTestimonials,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useSubmitTestimonial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: testimonialApi.submitTestimonial,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["testimonials"] });
    },
  });
}

export function useUpdateTestimonialStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string;
      status: "approved" | "rejected";
    }) => testimonialApi.updateTestimonialStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["testimonials"] });
    },
  });
}

export function useDeleteTestimonial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: testimonialApi.deleteTestimonial,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["testimonials"] });
    },
  });
}
